import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { HiPlus, HiDocumentArrowDown, HiFunnel } from 'react-icons/hi2';
import { getAudits, deleteAudit, calculateRiskLevel } from '../../services/productAuditService';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import CreateAuditForm from './CreateAuditForm';
import AuditTable from './AuditTable';
import jsPDF from 'jspdf';
import { collection, getDocs } from 'firebase/firestore';
import { firestore } from '../../database/firebase';

const PROJECT_STAGES = [
  'Idea Validation',
  'Market Research',
  'Prototype Development',
  'Product Development',
  'MVP Launch',
];

const ProductDevelopmentAuditTool = ({ userRole }) => {
  const { currentUser } = useAuth();
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [organizations, setOrganizations] = useState(new Map());
  const [filters, setFilters] = useState({
    stage: '',
    dateFrom: '',
    dateTo: '',
  });
  const [showFilters, setShowFilters] = useState(false);

  // Check if user has access
  // Allowed roles: Admin, Moderator, Business Advisor (moderator), Coach, SuperAdmin
  const allowedRoles = ['admin', 'moderator', 'coach', 'superAdmin'];
  const userRoleLower = userRole?.toLowerCase();
  const currentUserRoleLower = currentUser?.role?.toLowerCase();
  const hasAccess =
    allowedRoles.includes(userRoleLower) || allowedRoles.includes(currentUserRoleLower);

  const loadAudits = useCallback(async () => {
    setLoading(true);
    try {
      const filterParams = {};
      if (filters.stage) filterParams.stage = filters.stage;
      if (filters.dateFrom) filterParams.dateFrom = filters.dateFrom;
      if (filters.dateTo) filterParams.dateTo = filters.dateTo;

      const fetchedAudits = await getAudits(filterParams);

      // Fetch organizations for audits
      const orgMap = new Map();
      const clientEmails = new Set(fetchedAudits.map(a => a.userEmail).filter(Boolean));

      if (clientEmails.size > 0) {
        const clientsRef = collection(firestore, 'clients');
        const clientsSnapshot = await getDocs(clientsRef);

        clientsSnapshot.forEach(doc => {
          const clientData = doc.data();
          if (clientData.email && clientEmails.has(clientData.email)) {
            orgMap.set(clientData.email, clientData.organization || 'N/A');
          }
        });
      }

      // Enrich audits with organization data
      const enrichedAudits = fetchedAudits.map(audit => ({
        ...audit,
        organization: audit.organization || orgMap.get(audit.userEmail) || 'N/A',
      }));

      setAudits(enrichedAudits);
      setOrganizations(orgMap);
    } catch (error) {
      toast.error('Error loading audits.');
    } finally {
      setLoading(false);
    }
  }, [filters.stage, filters.dateFrom, filters.dateTo]);

  useEffect(() => {
    if (hasAccess) {
      loadAudits();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAccess, filters.stage, filters.dateFrom, filters.dateTo]);

  const handleDelete = async id => {
    try {
      await deleteAudit(id);
      toast.success('Audit deleted successfully!');
      loadAudits();
    } catch (error) {
      toast.error('Error deleting audit.');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      stage: '',
      dateFrom: '',
      dateTo: '',
    });
  };

  const handleCreateSuccess = () => {
    loadAudits();
  };

  const handleExportPDF = () => {
    try {
      const doc = new jsPDF();
      let yPos = 20;

      // Title
      doc.setFontSize(18);
      doc.text('Product Development Audit Report', 14, yPos);
      yPos += 10;
      doc.setFontSize(10);
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, yPos);
      yPos += 10;

      audits.forEach((audit, index) => {
        if (yPos > 280) {
          doc.addPage();
          yPos = 20;
        }
        const title = audit.submissionTitle || audit.productName || 'Untitled';
        const org = audit.organization || 'N/A';
        doc.setFontSize(14);
        doc.text(`Audit #${index + 1}: ${title} (${audit.projectStage})`, 14, yPos);
        yPos += 7;
        doc.setFontSize(10);
        doc.text(`Organization: ${org}`, 14, yPos);
        yPos += 5;
        doc.text(`Date: ${new Date(audit.auditDate).toLocaleDateString()}`, 14, yPos);
        yPos += 5;
        doc.text(`Created By: ${audit.createdBy} (${audit.role})`, 14, yPos);
        yPos += 5;
        doc.text(`Risks: ${audit.risks}`, 14, yPos);
        yPos += 5;
        doc.text(`Recommendations: ${audit.recommendations}`, 14, yPos);
        yPos += 10;
      });

      doc.save('product_development_audit_report.pdf');
      toast.success('PDF report generated successfully!');
    } catch (error) {
      toast.error('Error generating PDF report.');
    }
  };

  if (!hasAccess) {
    return (
      <div className="flex h-full items-center justify-center bg-white p-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600">Access Denied</h2>
          <p className="mt-2 text-gray-600">
            You do not have the necessary permissions to view this tool.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center bg-white p-6">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading audits...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-xl font-bold text-black md:text-2xl">Audit Tool</h2>
          <p className="text-sm text-gray-600">Manage and track product development audits</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
          >
            <HiFunnel className="h-4 w-4" />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
          <button
            onClick={handleExportPDF}
            className="flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
          >
            <HiDocumentArrowDown className="h-4 w-4" />
            Generate Report
          </button>
          <button
            onClick={() => setCreateModalOpen(true)}
            className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
          >
            <HiPlus className="h-5 w-5" />
            Create Audit
          </button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4"
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Stage</label>
              <select
                value={filters.stage}
                onChange={e => handleFilterChange('stage', e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              >
                <option value="">All Stages</option>
                {PROJECT_STAGES.map(stage => (
                  <option key={stage} value={stage}>
                    {stage}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Date From</label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={e => handleFilterChange('dateFrom', e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Date To</label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={e => handleFilterChange('dateTo', e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              />
            </div>

            <div className="flex items-end">
              <button
                onClick={clearFilters}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Stats */}
      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-4">
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-sm text-gray-600">Total Audits</p>
          <p className="mt-2 text-2xl font-semibold text-black">{audits.length}</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-sm text-gray-600">High Risk</p>
          <p className="mt-2 text-2xl font-semibold text-red-600">
            {
              audits.filter(
                a => calculateRiskLevel(a) === 'High' || calculateRiskLevel(a) === 'Critical'
              ).length
            }
          </p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-sm text-gray-600">This Month</p>
          <p className="mt-2 text-2xl font-semibold text-black">
            {
              audits.filter(a => {
                const auditDate = a.auditDate?.toDate
                  ? a.auditDate.toDate()
                  : new Date(a.auditDate);
                const now = new Date();
                return (
                  auditDate.getMonth() === now.getMonth() &&
                  auditDate.getFullYear() === now.getFullYear()
                );
              }).length
            }
          </p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-sm text-gray-600">Active Products</p>
          <p className="mt-2 text-2xl font-semibold text-black">
            {new Set(audits.map(a => a.productName)).size}
          </p>
        </div>
      </div>

      {/* Audit Table */}
      <AuditTable audits={audits} onDelete={handleDelete} onEdit={loadAudits} loading={loading} />

      {/* Create Modal */}
      <CreateAuditForm
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={handleCreateSuccess}
      />
    </div>
  );
};

export default ProductDevelopmentAuditTool;
